export default class TerritoryFieldRenderer {
  constructor(ctx) {
    this.ctx = ctx
  }

  draw({ territories, camera, state, atmosphere, viewport, time }) {
    this.ctx.save()

    territories.forEach(territory => {
      const center = camera.worldToScreen(territory.center.x, territory.center.y)
      const radius = Math.max(territory.radius * camera.zoom, 80)
      const isSelected = territory.id === state.selectedClusterId

      if (!this.isVisible(center, radius, viewport)) {
        return
      }

      this.ctx.save()
      this.applyMaterialIdentity(territory, center, radius, time, isSelected, atmosphere)
      this.ctx.restore()
    })

    this.ctx.restore()
  }

  applyMaterialIdentity(territory, center, radius, time, isSelected, atmosphere) {
    const primaryAxis = territory.dominantAxes[0]?.axis || 'warmth'
    const tension = territory.tension || 0
    const cohesion = territory.cohesion || 0

    // Determine base style from dominant axis
    if (primaryAxis === 'sacredness') {
      this.drawSacred(center, radius, territory.color, time, isSelected, atmosphere)
    } else if (primaryAxis === 'corporateDensity') {
      this.drawCorporate(center, radius, territory.color, time, isSelected, atmosphere)
    } else if (primaryAxis === 'nostalgia' || primaryAxis === 'isolation') {
      this.drawRuin(center, radius, territory.color, time, isSelected, atmosphere, tension)
    } else if (primaryAxis === 'surrealism') {
      this.drawSurreal(center, radius, territory.color, time, isSelected, atmosphere)
    } else if (primaryAxis === 'organicness' || primaryAxis === 'warmth') {
      this.drawPastoral(center, radius, territory.color, time, isSelected, atmosphere)
    } else if (primaryAxis === 'futurity' || primaryAxis === 'chaos') {
      this.drawCyberpunk(center, radius, territory.color, time, isSelected, atmosphere, tension)
    } else {
      this.drawDefault(center, radius, territory.color, time, isSelected, atmosphere)
    }

    // Overlay tension interference if high tension
    if (tension > 0.4) {
      this.drawTensionInterference(center, radius, territory.color, time, tension)
    }
  }

  drawSacred(center, radius, color, time, isSelected, atmosphere) {
    const pulse = Math.sin(time * 0.4) * 0.1 + 0.9
    const alpha = (isSelected ? 0.2 : 0.1) * pulse + (atmosphere.glow * 0.05)
    
    const gradient = this.ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, radius * 1.2)
    gradient.addColorStop(0, this.toRgba(color, alpha))
    gradient.addColorStop(0.4, this.toRgba(color, alpha * 0.5))
    gradient.addColorStop(1, this.toRgba(color, 0))

    this.ctx.fillStyle = gradient
    this.ctx.beginPath()
    this.ctx.arc(center.x, center.y, radius * 1.2, 0, Math.PI * 2)
    this.ctx.fill()

    // Radiant halo
    this.ctx.strokeStyle = this.toRgba('#fff', alpha * 0.2)
    this.ctx.lineWidth = 2
    this.ctx.beginPath()
    this.ctx.arc(center.x, center.y, radius * (0.8 + Math.sin(time * 0.2) * 0.05), 0, Math.PI * 2)
    this.ctx.stroke()
  }

  drawCorporate(center, radius, color, time, isSelected, atmosphere) {
    const alpha = isSelected ? 0.25 : 0.15
    const gradient = this.ctx.createRadialGradient(center.x, center.y, radius * 0.2, center.x, center.y, radius)
    gradient.addColorStop(0, this.toRgba(color, alpha))
    gradient.addColorStop(1, this.toRgba(color, 0.02))

    this.ctx.fillStyle = gradient
    this.ctx.beginPath()
    this.ctx.rect(center.x - radius, center.y - radius, radius * 2, radius * 2)
    this.ctx.clip()
    
    // Grid pattern
    this.ctx.strokeStyle = this.toRgba(color, alpha * 0.3)
    this.ctx.lineWidth = 1
    const gridSize = radius / 5
    for (let x = -radius; x <= radius; x += gridSize) {
      this.ctx.beginPath()
      this.ctx.moveTo(center.x + x, center.y - radius)
      this.ctx.lineTo(center.x + x, center.y + radius)
      this.ctx.stroke()
    }
    for (let y = -radius; y <= radius; y += gridSize) {
      this.ctx.beginPath()
      this.ctx.moveTo(center.x - radius, center.y + y)
      this.ctx.lineTo(center.x + radius, center.y + y)
      this.ctx.stroke()
    }

    this.ctx.fill()
  }

  drawRuin(center, radius, color, time, isSelected, atmosphere, tension) {
    const alpha = (isSelected ? 0.18 : 0.1) + (atmosphere.glow * 0.03)
    
    const gradient = this.ctx.createRadialGradient(center.x, center.y, radius * 0.1, center.x, center.y, radius)
    gradient.addColorStop(0, this.toRgba(color, alpha))
    gradient.addColorStop(1, this.toRgba(color, 0))

    this.ctx.fillStyle = gradient
    
    // Fragmented shape
    this.ctx.beginPath()
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2
      const dist = radius * (0.8 + Math.random() * 0.4)
      const x = center.x + Math.cos(angle) * dist
      const y = center.y + Math.sin(angle) * dist
      if (i === 0) this.ctx.moveTo(x, y)
      else this.ctx.lineTo(x, y)
    }
    this.ctx.closePath()
    this.ctx.fill()

    // Dithered grain
    this.ctx.fillStyle = this.toRgba('#000', 0.2)
    for (let i = 0; i < 100; i++) {
      const x = center.x + (Math.random() - 0.5) * radius * 1.5
      const y = center.y + (Math.random() - 0.5) * radius * 1.5
      this.ctx.fillRect(x, y, 1, 1)
    }
  }

  drawSurreal(center, radius, color, time, isSelected, atmosphere) {
    const alpha = isSelected ? 0.22 : 0.12
    const offset = Math.sin(time) * (radius * 0.1)
    
    const g1 = this.ctx.createRadialGradient(center.x + offset, center.y, 0, center.x + offset, center.y, radius)
    g1.addColorStop(0, this.toRgba(color, alpha))
    g1.addColorStop(1, this.toRgba(color, 0))

    const g2 = this.ctx.createRadialGradient(center.x - offset, center.y, 0, center.x - offset, center.y, radius)
    g2.addColorStop(0, this.toRgba('#8aa4ff', alpha * 0.8))
    g2.addColorStop(1, this.toRgba('#8aa4ff', 0))

    this.ctx.globalCompositeOperation = 'screen'
    this.ctx.fillStyle = g1
    this.ctx.beginPath()
    this.ctx.arc(center.x + offset, center.y, radius, 0, Math.PI * 2)
    this.ctx.fill()

    this.ctx.fillStyle = g2
    this.ctx.beginPath()
    this.ctx.arc(center.x - offset, center.y, radius, 0, Math.PI * 2)
    this.ctx.fill()
  }

  drawPastoral(center, radius, color, time, isSelected, atmosphere) {
    const alpha = (isSelected ? 0.2 : 0.12) + (atmosphere.glow * 0.04)
    const bloom = 1.1 + Math.sin(time * 0.3) * 0.05

    const gradient = this.ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, radius * bloom)
    gradient.addColorStop(0, this.toRgba(color, alpha))
    gradient.addColorStop(0.6, this.toRgba(color, alpha * 0.4))
    gradient.addColorStop(1, this.toRgba(color, 0))

    this.ctx.fillStyle = gradient
    this.ctx.beginPath()
    this.ctx.arc(center.x, center.y, radius * bloom, 0, Math.PI * 2)
    this.ctx.fill()
  }

  drawCyberpunk(center, radius, color, time, isSelected, atmosphere, tension) {
    const alpha = isSelected ? 0.24 : 0.14
    const flicker = Math.random() > 0.98 ? 1.5 : 1.0
    
    const gradient = this.ctx.createRadialGradient(center.x, center.y, radius * 0.3, center.x, center.y, radius)
    gradient.addColorStop(0, this.toRgba(color, alpha * flicker))
    gradient.addColorStop(1, this.toRgba(color, 0))

    this.ctx.fillStyle = gradient
    this.ctx.beginPath()
    this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2)
    this.ctx.fill()

    // Scanline scan
    const scanPos = (time * 50) % (radius * 2)
    this.ctx.strokeStyle = this.toRgba(color, alpha * 0.5)
    this.ctx.lineWidth = 2
    this.ctx.beginPath()
    this.ctx.moveTo(center.x - radius, center.y - radius + scanPos)
    this.ctx.lineTo(center.x + radius, center.y - radius + scanPos)
    this.ctx.stroke()
  }

  drawDefault(center, radius, color, time, isSelected, atmosphere) {
    const alpha = isSelected ? 0.16 : 0.08
    const gradient = this.ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, radius)
    gradient.addColorStop(0, this.toRgba(color, alpha))
    gradient.addColorStop(1, this.toRgba(color, 0))

    this.ctx.fillStyle = gradient
    this.ctx.beginPath()
    this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2)
    this.ctx.fill()
  }

  drawTensionInterference(center, radius, color, time, tension) {
    const alpha = (tension - 0.4) * 0.2
    this.ctx.save()
    this.ctx.translate(center.x, center.y)
    this.ctx.rotate(time * 0.1)
    
    this.ctx.strokeStyle = this.toRgba('#fff', alpha)
    this.ctx.lineWidth = 1
    for (let i = 0; i < 3; i++) {
      const r = radius * (0.9 + i * 0.1 + Math.sin(time * 2 + i) * 0.05)
      this.ctx.beginPath()
      this.ctx.arc(0, 0, r, 0, Math.PI * 0.5)
      this.ctx.stroke()
    }
    this.ctx.restore()
  }

  isVisible(center, radius, viewport) {
    const margin = radius * 1.5
    return center.x + margin > 0
      && center.x - margin < viewport.width
      && center.y + margin > 0
      && center.y - margin < viewport.height
  }

  toRgba(color, alpha) {
    if (color.startsWith('rgb(')) {
      return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`)
    }

    const normalized = color.replace('#', '')
    const value = normalized.length === 3
      ? normalized.split('').map(char => char + char).join('')
      : normalized
    const int = Number.parseInt(value, 16)

    return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`
  }
}
